'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { cinzel, greatVibes } from '@/lib/fonts';
import CaseStudyStory from '@/components/our-work/CaseStudyStory';

export default function Design1StudioPage() {
    const colorsSectionRef = useRef<HTMLDivElement>(null);
    const swatchRefs = useRef<(HTMLDivElement | null)[]>([]);
    const colorsHeadingRef = useRef<HTMLHeadingElement>(null);
    const colorsDescRef = useRef<HTMLDivElement>(null);

    // Logo section refs
    const logoBoxRef = useRef<HTMLDivElement>(null);
    const logoImgRef = useRef<HTMLDivElement>(null);

    // Website section refs
    const websiteEyebrowRef = useRef<HTMLParagraphElement>(null);
    const websiteHeadingRef = useRef<HTMLHeadingElement>(null);
    const websiteDescRef = useRef<HTMLDivElement>(null);
    const screenshotRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('d1s-animate-in');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12 }
        );

        const els = [
            colorsHeadingRef.current,
            colorsDescRef.current,
            logoBoxRef.current,
            logoImgRef.current,
            websiteEyebrowRef.current,
            websiteHeadingRef.current,
            websiteDescRef.current,
        ];
        els.forEach((el) => { if (el) observer.observe(el); });
        swatchRefs.current.forEach((el) => { if (el) observer.observe(el); });
        screenshotRefs.current.forEach((el) => { if (el) observer.observe(el); });

        return () => observer.disconnect();
    }, []);

    return (
        <div className={`${cinzel.variable} ${greatVibes.variable}`}>
            <style>{`
          /* ─── Keyframes ─── */
          @keyframes d1s-fade-up {
            from { opacity: 0; transform: translateY(40px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes d1s-slide-left {
            from { opacity: 0; transform: translateX(-60px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes d1s-slide-right {
            from { opacity: 0; transform: translateX(60px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes d1s-swatch-reveal {
            from { clip-path: inset(0 100% 0 0); opacity: 0; }
            to   { clip-path: inset(0 0% 0 0);   opacity: 1; }
          }
          @keyframes d1s-hex-pop {
            from { opacity: 0; transform: scale(0.85); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes d1s-screenshot-rise {
            from { opacity: 0; transform: translateY(60px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }

          /* ─── Initial hidden states ─── */
          .d1s-colors-heading,
          .d1s-colors-desc,
          .d1s-swatch-wrap,
          .d1s-logo-box-anim,
          .d1s-logo-img-anim,
          .d1s-website-eyebrow,
          .d1s-website-heading,
          .d1s-website-desc,
          .d1s-screenshot-card { opacity: 0; }

          /* ─── Animate-in triggers ─── */
          .d1s-colors-heading.d1s-animate-in {
            animation: d1s-fade-up 0.65s cubic-bezier(0.22,1,0.36,1) 0.05s both;
          }
          .d1s-colors-desc.d1s-animate-in {
            animation: d1s-fade-up 0.65s cubic-bezier(0.22,1,0.36,1) 0.2s both;
          }
          .d1s-swatch-wrap.d1s-animate-in {
            animation: d1s-swatch-reveal 0.8s cubic-bezier(0.22,1,0.36,1) both;
          }
          .d1s-swatch-wrap.d1s-animate-in .d1s-hex-label {
            animation: d1s-hex-pop 0.5s cubic-bezier(0.22,1,0.36,1) both;
          }
          .d1s-swatch-wrap:nth-child(1).d1s-animate-in { animation-delay: 0.25s; }
          .d1s-swatch-wrap:nth-child(1).d1s-animate-in .d1s-hex-label { animation-delay: 0.75s; }
          .d1s-swatch-wrap:nth-child(2).d1s-animate-in { animation-delay: 0.45s; }
          .d1s-swatch-wrap:nth-child(2).d1s-animate-in .d1s-hex-label { animation-delay: 0.95s; }
          .d1s-swatch-wrap:nth-child(3).d1s-animate-in { animation-delay: 0.65s; }
          .d1s-swatch-wrap:nth-child(3).d1s-animate-in .d1s-hex-label { animation-delay: 1.15s; }

          /* Logo section */
          .d1s-logo-box-anim.d1s-animate-in {
            animation: d1s-slide-left 0.75s cubic-bezier(0.22,1,0.36,1) 0.1s both;
          }
          .d1s-logo-img-anim.d1s-animate-in {
            animation: d1s-slide-right 0.75s cubic-bezier(0.22,1,0.36,1) 0.25s both;
          }

          /* Website section */
          .d1s-website-eyebrow.d1s-animate-in {
            animation: d1s-fade-up 0.55s cubic-bezier(0.22,1,0.36,1) 0s both;
          }
          .d1s-website-heading.d1s-animate-in {
            animation: d1s-fade-up 0.65s cubic-bezier(0.22,1,0.36,1) 0.12s both;
          }
          .d1s-website-desc.d1s-animate-in {
            animation: d1s-fade-up 0.65s cubic-bezier(0.22,1,0.36,1) 0.25s both;
          }

          /* Screenshot cards — staggered */
          .d1s-screenshot-card.d1s-animate-in {
            animation: d1s-screenshot-rise 0.75s cubic-bezier(0.22,1,0.36,1) both;
          }
          .d1s-screenshot-card:nth-child(1).d1s-animate-in { animation-delay: 0.1s; }
          .d1s-screenshot-card:nth-child(2).d1s-animate-in { animation-delay: 0.25s; }
          .d1s-screenshot-card:nth-child(3).d1s-animate-in { animation-delay: 0.4s; }
          .d1s-screenshot-card:nth-child(4).d1s-animate-in { animation-delay: 0.55s; }

          /* ─── Hover effects ─── */
          .d1s-swatch-wrap {
            cursor: default;
            transition: transform 0.35s cubic-bezier(0.22,1,0.36,1),
                        box-shadow 0.35s cubic-bezier(0.22,1,0.36,1);
          }
          .d1s-swatch-wrap:hover {
            transform: scaleY(1.06) translateY(-2px);
            box-shadow: 0 12px 40px rgba(64,36,25,0.18);
            z-index: 2;
          }
          .d1s-screenshot-card {
            transition: transform 0.4s cubic-bezier(0.22,1,0.36,1),
                        box-shadow 0.4s cubic-bezier(0.22,1,0.36,1);
            border-radius: 16px;
            overflow: hidden;
          }
          .d1s-screenshot-card:hover {
            transform: translateY(-10px) scale(1.02);
            box-shadow: 0 24px 60px rgba(64,36,25,0.18);
            z-index: 2;
          }

          /* ─── Responsive ─── */
          @media (max-width: 768px) {
            .d1s-card { max-width: calc(100% - 32px) !important; border-radius: 40px !important; }
            .d1s-hero { height: 200px !important; }
            .d1s-logo-section { flex-direction: column !important; padding: 0 12px !important; margin-top: 24px !important; }
            .d1s-intro { padding: 40px 24px 8px !important; }
            .d1s-logo-box { width: 100% !important; padding: 32px 24px !important; }
            .d1s-logo-img { width: 100% !important; aspect-ratio: 4/3 !important; margin-top: 16px !important; }
            .d1s-section { padding: 40px 20px 0 !important; }
            .d1s-website-section { padding: 40px 20px 40px !important; }
            .d1s-swatch { height: 55px !important; }
            .d1s-screenshots { gap: 10px !important; }
            .d1s-screenshots > div { width: 48% !important; }
          }
        `}</style>
            <main style={{ background: '#080400', minHeight: '100vh', paddingBottom: 80 }}>

                {/* ── Outer black top padding (navbar area) ── */}
                <div style={{ height: 112 }} />

                {/* ── White rounded card ── */}
                <div className="d1s-card" style={{
                    background: '#fbfdfd',
                    borderRadius: 100,
                    width: 1798,
                    maxWidth: 'calc(100% - 122px)',
                    margin: '0 auto',
                    overflow: 'hidden',
                    position: 'relative',
                }}>

                    {/* ── Hero Video ── */}
                    <div className="d1s-hero" style={{
                        width: '100%',
                        height: 642,
                        overflow: 'hidden',
                        position: 'relative',
                        background: '#e8e5df',
                    }}>
                        <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'top center',
                                display: 'block',
                            }}
                        >
                            <source src="/videos/design1studio-hero.mp4" type="video/mp4" />
                        </video>
                    </div>

                    {/* ── Page title ── */}
                    <div className="d1s-intro" style={{ padding: '72px 80px 20px', boxSizing: 'border-box' }}>
                        <h1 style={{
                            fontFamily: 'var(--font-cinzel), Cinzel, serif',
                            fontWeight: 700,
                            fontSize: 'clamp(26px, 4.2vw, 68px)',
                            letterSpacing: '0.1em',
                            color: '#402419',
                            margin: 0,
                            lineHeight: 1.1,
                        }}>
                            Design<span style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontWeight: 800 }}>1</span>Studio
                            {' '}
                            <span style={{
                                display: 'block',
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 600,
                                fontSize: 'clamp(13px, 1.2vw, 18px)',
                                letterSpacing: '0.08em',
                                color: '#7a5a45',
                                marginTop: 12,
                            }}>
                                Brand Identity &amp; Website Design Case Study
                            </span>
                        </h1>
                        <CaseStudyStory
                            problem="Design1Studio is an interior design studio in Bangalore. It needed a brand and website that reflected its minimal, refined design philosophy, and a way to turn online attention into qualified interior design enquiries."
                            approach={[
                                'Created the brand identity: logo, colour palette and visual system.',
                                'Designed and built a minimal, project-led website that puts the studio’s work first.',
                                'Ran a two-stage Meta strategy: video campaigns to build awareness, then retargeting of engaged viewers for enquiries.',
                                'Managed Design1Studio’s Instagram with project showcases and SEO-led content planning.',
                            ]}
                            results={[
                                { num: '6.8×', label: 'Return on ad spend from Meta lead campaigns', source: 'Meta Ads Manager' },
                                { num: '1M+', label: 'Video views from the Meta awareness campaign, building a large retargeting audience', source: 'Meta Ads Manager' },
                            ]}
                            services={[
                                { label: 'Brand Strategy', href: '/services/brand-strategy/' },
                                { label: 'Website Development', href: '/services/web-development/' },
                                { label: 'Meta Ads', href: '/services/performance-marketing/meta-ads/' },
                                { label: 'Social Media', href: '/services/social-media/' },
                            ]}
                            theme={{ ink: '#402419', body: '#080400', accent: '#a0784f', panel: '#402419', panelInk: '#e9dfd0', panelAccent: '#e0c58f' }}
                        />
                    </div>

                    {/* ── Brand Logo Section ── */}
                    <div className="d1s-logo-section" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 22px',
                        marginTop: 51,
                        gap: 26,
                    }}>
                        {/* Left dark brown box */}
                        <div
                            ref={logoBoxRef}
                            className="d1s-logo-box d1s-logo-box-anim"
                            style={{
                                background: '#402419',
                                flex: '0 0 auto',
                                width: '52%',
                                position: 'relative',
                                padding: '60px 64px',
                                boxSizing: 'border-box',
                                alignSelf: 'flex-start',
                            }}
                        >
                            {/* BRAND LOGO heading */}
                            <h2 style={{
                                fontFamily: 'var(--font-cinzel), Cinzel, serif',
                                fontWeight: 700,
                                fontSize: 'clamp(22px, 3vw, 48px)',
                                letterSpacing: '0.13em',
                                color: '#e9dfd0',
                                margin: '0 0 20px',
                                lineHeight: 'normal',
                            }}>
                                BRAND{' '}
                                <span style={{
                                    fontFamily: 'var(--font-great-vibes), "Great Vibes", cursive',
                                    fontWeight: 400,
                                    fontSize: 'clamp(26px, 3.5vw, 58px)',
                                    color: '#e0c58f',
                                    fontStyle: 'normal',
                                    letterSpacing: '0.13em',
                                }}>
                                    Logo
                                </span>
                            </h2>

                            {/* Description */}
                            <div style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 400,
                                fontSize: 'clamp(13px, 1.2vw, 18px)',
                                color: '#f5fde9',
                                letterSpacing: '0.05em',
                                lineHeight: 1.8,
                                maxWidth: 958,
                            }}>
                                <p style={{ margin: '0 0 16px' }}>
                                    The Design1Studio logo is crafted with a focus on simplicity, precision, and modern aesthetics. Its clean and minimal form reflects the brand&apos;s design philosophy, making it instantly recognizable and adaptable across various applications.
                                </p>
                                <p style={{ margin: '0 0 16px' }}>
                                    The logo is designed to work seamlessly with the refined color palette and typography, ensuring consistency across all brand touchpoints. Its balanced structure and subtle detailing create a strong visual identity without overwhelming the overall design.
                                </p>
                                <p style={{ margin: 0 }}>
                                    This approach allows the logo to represent the brand with clarity, professionalism, and a contemporary edge.
                                </p>
                            </div>
                        </div>

                        {/* Right logo image */}
                        <div
                            ref={logoImgRef}
                            className="d1s-logo-img d1s-logo-img-anim"
                            style={{
                                flexShrink: 0,
                                width: 595,
                                height: 483,
                                aspectRatio: '85 / 69',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                            }}
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src="/images/our-work/d1s-logo.webp"
                                alt="Design1Studio logo"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    objectPosition: 'left center',
                                    display: 'block',
                                }}
                            />
                        </div>
                    </div>

                    {/* ── Brand Colors Section ── */}
                    <div ref={colorsSectionRef} className="d1s-section" style={{
                        padding: '80px 44px 0',
                        boxSizing: 'border-box',
                    }}>
                        {/* Heading */}
                        <h2 ref={colorsHeadingRef} className="d1s-colors-heading" style={{
                            fontFamily: 'var(--font-cinzel), Cinzel, serif',
                            fontWeight: 700,
                            fontSize: 'clamp(22px, 3vw, 48px)',
                            letterSpacing: '0.13em',
                            color: '#402419',
                            margin: '0 0 20px',
                            lineHeight: 'normal',
                        }}>
                            BRAND{' '}
                            <span style={{
                                fontFamily: 'var(--font-great-vibes), "Great Vibes", cursive',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                fontSize: 'clamp(26px, 3.5vw, 58px)',
                                letterSpacing: '0.13em',
                                textTransform: 'capitalize',
                            }}>
                                Colors
                            </span>
                        </h2>

                        {/* Description */}
                        <div ref={colorsDescRef} className="d1s-colors-desc" style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                            fontWeight: 400,
                            fontSize: 'clamp(13px, 1.2vw, 18px)',
                            color: '#080400',
                            letterSpacing: '0.05em',
                            lineHeight: 1.8,
                            marginBottom: 40,
                        }}>
                            <p style={{ margin: '0 0 0' }}>
                                The color palette for Design1Studio blends rich, deep tones with soft neutrals to create a warm and sophisticated identity. A strong brown base adds depth and elegance, while lighter shades bring balance and clarity.
                            </p>
                            <p style={{ margin: 0 }}>
                                Together, these colors form a cohesive and modern aesthetic that enhances readability, maintains consistency, and strengthens the overall brand presence.
                            </p>
                        </div>

                        {/* Color swatches */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 21 }}>
                            {[
                                { bg: '#402419', hex: '#402419', textColor: '#fbfdfd', fontWeight: 300 },
                                { bg: '#e9dfd0', hex: '#E9DFD0', textColor: '#080400', fontWeight: 400 },
                                { bg: '#f3ece2', hex: '#F3ECE2', textColor: '#080400', fontWeight: 400 },
                            ].map(({ bg, hex, textColor, fontWeight }, i) => (
                                <div
                                    key={hex}
                                    ref={(el) => { swatchRefs.current[i] = el; }}
                                    className="d1s-swatch-wrap d1s-swatch"
                                    style={{
                                        background: bg,
                                        height: 90,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        transformOrigin: 'center center',
                                    }}
                                >
                                    <span className="d1s-hex-label" style={{
                                        fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                        fontWeight,
                                        fontSize: 'clamp(15px, 2vw, 32px)',
                                        color: textColor,
                                        letterSpacing: '0.13em',
                                    }}>
                                        {hex}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Website Design Section ── */}
                    <div className="d1s-website-section" style={{
                        padding: '80px 44px 80px',
                        boxSizing: 'border-box',
                    }}>
                        {/* Eyebrow label */}
                        <p
                            ref={websiteEyebrowRef}
                            className="d1s-website-eyebrow"
                            style={{
                                fontFamily: 'var(--font-great-vibes), "Great Vibes", cursive',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                fontSize: 'clamp(22px, 2.4vw, 38px)',
                                color: '#402419',
                                letterSpacing: '0.15em',
                                textTransform: 'lowercase',
                                margin: '0 0 0',
                                lineHeight: 1.4,
                            }}
                        >
                            website design
                        </p>

                        {/* Main heading */}
                        <h2
                            ref={websiteHeadingRef}
                            className="d1s-website-heading"
                            style={{
                                fontFamily: 'var(--font-cinzel), Cinzel, serif',
                                fontWeight: 700,
                                fontSize: 'clamp(26px, 3.4vw, 56px)',
                                letterSpacing: '0.13em',
                                color: '#402419',
                                margin: '16px 0',
                                lineHeight: 'normal',
                            }}
                        >
                            Design1
                            <span style={{
                                fontFamily: 'var(--font-great-vibes), "Great Vibes", cursive',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                fontSize: 'clamp(30px, 4vw, 66px)',
                            }}>
                                Studio
                            </span>
                        </h2>

                        {/* Description */}
                        <div
                            ref={websiteDescRef}
                            className="d1s-website-desc"
                            style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 400,
                                fontSize: 'clamp(14px, 1.3vw, 20px)',
                                color: '#080400',
                                letterSpacing: '0.04em',
                                lineHeight: 1.85,
                                marginBottom: 48,
                                maxWidth: 1589,
                            }}
                        >
                            <p style={{ margin: '0 0 16px' }}>
                                Design1Studio required a website that reflects its design philosophy — minimal, refined, and user-focused. Our approach was to create a digital experience that communicates its work with clarity and sophistication.
                            </p>
                            <p style={{ margin: '0 0 16px' }}>
                                From layout structure to interaction design, we built a platform that enhances usability while maintaining a strong visual identity.
                            </p>
                            <p style={{ margin: 0 }}>
                                Discover the website we crafted for the brand.
                            </p>
                        </div>

                        {/* 4 mobile screenshots */}
                        <div className="d1s-screenshots" style={{
                            display: 'flex',
                            gap: 32,
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                        }}>
                            {/* Screenshot 1 */}
                            <div
                                ref={(el) => { screenshotRefs.current[0] = el; }}
                                className="d1s-screenshot-card"
                                style={{
                                    flex: '0 0 auto',
                                    width: '23%',
                                    aspectRatio: '368 / 1052',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/d1s-homepage.webp"
                                    alt="Design1Studio homepage design, upper half: hero, services and before/after projects"
                                    style={{
                                        width: '100.04%',
                                        height: '206.77%',
                                        position: 'absolute',
                                        left: '-0.02%',
                                        top: 0,
                                        maxWidth: 'none',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>

                            {/* Screenshot 2 */}
                            <div
                                ref={(el) => { screenshotRefs.current[1] = el; }}
                                className="d1s-screenshot-card"
                                style={{
                                    flex: '0 0 auto',
                                    width: '21%',
                                    aspectRatio: '336 / 1052',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/d1s-homepage.webp"
                                    alt="Design1Studio homepage design, lower half: studio stats, client reviews, FAQs and brand partners"
                                    style={{
                                        width: '100.04%',
                                        height: '188.57%',
                                        position: 'absolute',
                                        left: '-0.02%',
                                        top: '-88.73%',
                                        maxWidth: 'none',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>

                            {/* Screenshot 3 */}
                            <div
                                ref={(el) => { screenshotRefs.current[2] = el; }}
                                className="d1s-screenshot-card"
                                style={{
                                    flex: '0 0 auto',
                                    width: '23%',
                                    aspectRatio: '365 / 1052',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/d1s-aboutus.webp"
                                    alt="Design1Studio About page design: studio story and what makes them different"
                                    style={{
                                        width: '100%',
                                        height: '186.15%',
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        maxWidth: 'none',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>

                            {/* Screenshot 4 */}
                            <div
                                ref={(el) => { screenshotRefs.current[3] = el; }}
                                className="d1s-screenshot-card"
                                style={{
                                    flex: '0 0 auto',
                                    width: '23%',
                                    aspectRatio: '367 / 609',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/d1s-aboutus.webp"
                                    alt="Design1Studio About page design: services, contact form and studio photos"
                                    style={{
                                        width: '100%',
                                        height: '323.28%',
                                        position: 'absolute',
                                        left: 0,
                                        top: '-171.15%',
                                        maxWidth: 'none',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                </div>

                {/* ── Back link ── */}
                <div style={{ textAlign: 'center', marginTop: 60 }}>
                    <Link
                        href="/our-work"
                        style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                            fontWeight: 500,
                            fontSize: 16,
                            color: '#fbfdfd',
                            opacity: 0.5,
                            textDecoration: 'none',
                            letterSpacing: '0.05em',
                            transition: 'opacity 0.2s ease',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.5'; }}
                    >
                        ← Back to Our Work
                    </Link>
                </div>

            </main>
        </div>
    );
}
